import { type ReactNode, useEffect } from 'react';
import { Trans } from '@lingui/react/macro';
import SidebarNavigation from './SidebarNavigation';
import SidebarNavigationSkeleton from './SidebarNavigation/SidebarNavigationSkeleton';
import LoadingScreen from './LoadingScreen';
import { useCurrentUser } from '../services/userHooks';
import { useMobileNavSidebar } from '../hooks/useUserPreferences';
import { isReturningUser } from '../utils/returningUser';
import { BookmarkDndProvider } from '../contexts/BookmarkDndContext';
import { BookmarkSelectionProvider } from '../contexts/BookmarkSelectionContext';
import { BookmarkExitAnimationProvider } from '../contexts/BookmarkExitAnimationContext';
import { useFlashMessages } from '../hooks/useFlashMessages';
import { useExitBatchModeOnNavigate } from '../hooks/useExitBatchModeOnNavigate';

interface AppLayoutProps {
  children: ReactNode;
}

function BatchModeNavigationGuard() {
  useExitBatchModeOnNavigate();
  return null;
}

function AppLayout({ children }: AppLayoutProps) {
  const { currentUser, isLoading } = useCurrentUser();
  const { showMobileNavSidebar, closeMobileNavSidebar } = useMobileNavSidebar();

  // Load Typekit once per app for reader font (ff-tisa-web-pro) when tisa is a theme option
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//use.typekit.net/dkx1mpq.js';
    script.async = true;
    document.head.appendChild(script);
    script.onload = () => {
      try {
        // @ts-expect-error - Typekit is loaded globally
        window.Typekit?.load();
      } catch (e) {
        console.warn('Typekit failed to load:', e);
      }
    };
  }, []);

  // Handle flash messages from backend redirects for all authenticated pages
  useFlashMessages();

  const showShellSkeleton = (isLoading || !currentUser) && isReturningUser();

  if (showShellSkeleton) {
    return (
      <BookmarkSelectionProvider>
        <a
          href="#main-content"
          className="skip-to-content sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-black focus:px-4 focus:py-2 focus:text-white focus:outline-none"
        >
          <Trans>Skip to main content</Trans>
        </a>
        <div className="app-container flex">
          {showMobileNavSidebar && (
            <div
              className="z-sidebar-backdrop fixed inset-0 md:hidden"
              onClick={closeMobileNavSidebar}
            />
          )}

          <div
            className={`side-nav-container md:translate-x-0 ${showMobileNavSidebar ? 'translate-x-0' : '-translate-x-full'}`}
          >
            <SidebarNavigationSkeleton />
          </div>

          <main id="main-content" className="flex-fluid outline-none" aria-busy="true">
            {children}
          </main>
        </div>
      </BookmarkSelectionProvider>
    );
  }

  if (isLoading || !currentUser) {
    return <LoadingScreen />;
  }

  return (
    <BookmarkExitAnimationProvider>
      <BookmarkSelectionProvider>
        <BatchModeNavigationGuard />
        <BookmarkDndProvider>
          <a
            href="#main-content"
            className="skip-to-content sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-black focus:px-4 focus:py-2 focus:text-white focus:outline-none"
          >
            <Trans>Skip to main content</Trans>
          </a>
          <div className="app-container flex">
            {showMobileNavSidebar && (
              <div
                className="z-sidebar-backdrop fixed inset-0 md:hidden"
                onClick={closeMobileNavSidebar}
              />
            )}

            <div
              className={`side-nav-container md:translate-x-0 ${showMobileNavSidebar ? 'translate-x-0' : '-translate-x-full'}`}
            >
              <SidebarNavigation />
            </div>

            <main id="main-content" className="flex-fluid outline-none">
              {children}
            </main>
          </div>
        </BookmarkDndProvider>
      </BookmarkSelectionProvider>
    </BookmarkExitAnimationProvider>
  );
}

export default AppLayout;
